import { useMemo, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  MapPin, Route as RouteIcon, Calendar, Plus, Pencil, Trash2, Wand2, X, Loader2,
  Navigation, Clock, CheckCircle2, XCircle, Search, GripVertical, Save, Gauge,
  Users, TrendingUp, Footprints, Target, ArrowUp, ArrowDown,
} from 'lucide-react'
import { MapContainer, TileLayer, Polyline, CircleMarker, Tooltip as LTooltip, useMap } from 'react-leaflet'
import 'leaflet/dist/leaflet.css'
import toast from 'react-hot-toast'
import { api, formatMoney } from '@/lib/api'
import { enterAbrePrimeiro } from '@/lib/keyboard'
import { pedirExclusao } from '@/lib/confirmarExclusao'

const DIAS: [number | null, string][] = [
  [null, 'Sem dia fixo'], [0, 'Segunda'], [1, 'Terça'], [2, 'Quarta'],
  [3, 'Quinta'], [4, 'Sexta'], [5, 'Sábado'], [6, 'Domingo'],
]
const diaLabel = (d: number | null | undefined) => DIAS.find(x => x[0] === (d ?? null))?.[1] ?? '—'
const hojeISO = () => { const d = new Date(); d.setHours(d.getHours() - 3); return d.toISOString().slice(0, 10) }
const fmtDur = (s?: number | null) => {
  if (!s) return '—'
  const m = Math.round(s / 60)
  return m >= 60 ? `${Math.floor(m / 60)}h${String(m % 60).padStart(2, '0')}` : `${m} min`
}
const hhmm = (iso?: string | null) => (iso ? iso.slice(11, 16) : '')

// ============================================================================
export default function RotasComercialPage() {
  const [aba, setAba] = useState<'rotas' | 'mapa' | 'aderencia'>('rotas')
  return (
    <div className="p-4 lg:p-6 space-y-4 bg-ink-50/40 min-h-full">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2 text-ink-800">
          <RouteIcon className="text-violet-600" size={26} /> Rotas de Visita
        </h1>
        <p className="text-xs text-ink-500 mt-0.5">
          Roteiro de visita do vendedor (app de campo) — cadastre/edite as rotas, veja o
          <b> previsto × realizado</b> no mapa e a aderência. Os dados vêm do app do vendedor.
        </p>
      </div>

      <div className="flex gap-1 border-b border-ink-200">
        {([['rotas', 'Rotas'], ['mapa', 'Mapa previsto × realizado'], ['aderencia', 'Aderência']] as const).map(([k, l]) => (
          <button key={k} onClick={() => setAba(k)}
            className={`px-5 py-2 text-sm font-medium -mb-px border-b-2 transition ${
              aba === k ? 'border-violet-600 text-violet-700' : 'border-transparent text-ink-500 hover:text-ink-700'}`}>
            {l}
          </button>
        ))}
      </div>

      {aba === 'rotas' && <AbaRotas />}
      {aba === 'mapa' && <AbaMapa />}
      {aba === 'aderencia' && <AbaAderencia />}
    </div>
  )
}

// ============================================================================
// ABA 1 — CADASTRO DE ROTAS
// ============================================================================
function AbaRotas() {
  const qc = useQueryClient()
  const [edit, setEdit] = useState<any | null>(null)   // rota em edição (ou {} pra nova)

  const { data, isLoading } = useQuery<any>({
    queryKey: ['fv-rotas'],
    queryFn: async () => (await api.get('/forca-vendas/rotas')).data,
  })

  const del = useMutation({
    mutationFn: async (rid: number) => (await api.delete(`/forca-vendas/rotas/${rid}`)).data,
    onSuccess: () => { toast.success('Rota excluída'); qc.invalidateQueries({ queryKey: ['fv-rotas'] }) },
  })

  if (isLoading) return <CarregandoBox />
  const rotas = data?.rows || []

  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center">
        <span className="text-sm text-ink-500">{rotas.length} rota(s)</span>
        <button onClick={() => setEdit({})}
          className="flex items-center gap-1.5 bg-violet-600 text-white text-sm font-semibold rounded-lg px-3 py-2 hover:bg-violet-700">
          <Plus size={16} /> Nova rota
        </button>
      </div>

      <div className="bg-white rounded-2xl border border-ink-200 shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="text-ink-500 text-xs border-b bg-ink-50/60">
            <th className="text-left py-2 px-3">Rota</th><th className="text-left">Vendedor</th>
            <th className="text-left">Dia</th><th className="text-right">Clientes</th>
            <th className="text-center">Ativa</th><th className="text-right px-3">Ações</th>
          </tr></thead>
          <tbody>
            {rotas.map((r: any) => (
              <tr key={r.id} className="border-b last:border-0 hover:bg-ink-50">
                <td className="py-2 px-3 font-medium text-ink-800">
                  <span className="inline-block w-3 h-3 rounded-full mr-2 align-middle" style={{ background: r.cor }} />
                  {r.nome}
                </td>
                <td className="text-ink-600">{r.vendedor || <span className="text-ink-300">—</span>}</td>
                <td className="text-ink-500">{r.dia_label}</td>
                <td className="text-right font-semibold">{r.qt_clientes}</td>
                <td className="text-center">{r.ativo
                  ? <span className="text-emerald-600 text-xs font-semibold">●</span>
                  : <span className="text-ink-300 text-xs">○</span>}</td>
                <td className="text-right px-3 whitespace-nowrap">
                  <button onClick={() => setEdit(r)} className="text-ink-400 hover:text-violet-600 p-1"><Pencil size={15} /></button>
                  <button onClick={() => pedirExclusao({ descricao: `a rota "${r.nome}"`, onConfirm: () => del.mutate(r.id) })}
                    className="text-ink-400 hover:text-rose-600 p-1"><Trash2 size={15} /></button>
                </td>
              </tr>
            ))}
            {rotas.length === 0 && <tr><td colSpan={6}><Vazio txt="Nenhuma rota cadastrada" /></td></tr>}
          </tbody>
        </table>
      </div>

      {edit && <ModalRota rota={edit} onClose={() => setEdit(null)} />}
    </div>
  )
}

function ModalRota({ rota, onClose }: { rota: any; onClose: () => void }) {
  const qc = useQueryClient()
  const novo = !rota.id
  const [nome, setNome] = useState(rota.nome || '')
  const [userId, setUserId] = useState<string>(rota.user_id ? String(rota.user_id) : '')
  const [dia, setDia] = useState<string>(rota.dia_semana == null ? '' : String(rota.dia_semana))
  const [cor, setCor] = useState(rota.cor || '#5e1626')
  const [ativo, setAtivo] = useState(rota.ativo ?? true)

  const { data: vends } = useQuery<any>({
    queryKey: ['fv-vendedores'],
    queryFn: async () => (await api.get('/forca-vendas/vendedores')).data,
  })
  // detalhe c/ paradas (só edição)
  const { data: detalhe } = useQuery<any>({
    queryKey: ['fv-rota', rota.id],
    queryFn: async () => (await api.get(`/forca-vendas/rotas/${rota.id}`)).data,
    enabled: !!rota.id,
  })

  const save = useMutation({
    mutationFn: async () => {
      const body = { nome, user_id: userId ? Number(userId) : null, dia_semana: dia === '' ? null : Number(dia), cor, ativo }
      if (novo) return (await api.post('/forca-vendas/rotas', body)).data
      return (await api.put(`/forca-vendas/rotas/${rota.id}`, body)).data
    },
    onSuccess: (r) => {
      toast.success(novo ? 'Rota criada' : 'Rota salva')
      qc.invalidateQueries({ queryKey: ['fv-rotas'] })
      if (novo && r?.id) { rota.id = r.id }  // permite gerenciar paradas logo após criar
      else onClose()
    },
    onError: () => toast.error('Não foi possível salvar'),
  })

  return (
    <div className="fixed inset-0 z-40 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white w-full max-w-3xl rounded-2xl max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 bg-white px-5 py-3 border-b flex items-center justify-between">
          <h3 className="font-bold text-ink-800">{novo ? 'Nova rota' : `Editar — ${rota.nome}`}</h3>
          <button onClick={onClose} className="text-ink-400 hover:text-ink-700"><X size={20} /></button>
        </div>

        <div className="p-5 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Campo label="Nome da rota">
              <input value={nome} onChange={e => setNome(e.target.value)} className="input" placeholder="Ex.: ROTA 1: CRATO" />
            </Campo>
            <Campo label="Vendedor responsável">
              <select value={userId} onChange={e => setUserId(e.target.value)} className="input">
                <option value="">— sem vendedor —</option>
                {(vends?.rows || []).map((v: any) => <option key={v.id} value={v.id}>{v.nome}{v.codusur ? ` (cod ${v.codusur})` : ''}</option>)}
              </select>
            </Campo>
            <Campo label="Dia da semana">
              <select value={dia} onChange={e => setDia(e.target.value)} className="input">
                {DIAS.map(([k, l]) => <option key={String(k)} value={k == null ? '' : k}>{l}</option>)}
              </select>
            </Campo>
            <Campo label="Cor no mapa">
              <div className="flex items-center gap-2">
                <input type="color" value={cor} onChange={e => setCor(e.target.value)} className="h-9 w-14 rounded border border-ink-300" />
                <label className="flex items-center gap-1.5 text-sm text-ink-600 ml-2">
                  <input type="checkbox" checked={ativo} onChange={e => setAtivo(e.target.checked)} /> Ativa
                </label>
              </div>
            </Campo>
          </div>

          <button onClick={() => save.mutate()} disabled={save.isPending || !nome.trim()}
            className="flex items-center gap-2 bg-violet-600 text-white text-sm font-semibold rounded-lg px-4 py-2 hover:bg-violet-700 disabled:opacity-40">
            {save.isPending ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
            {novo ? 'Criar rota' : 'Salvar dados'}
          </button>

          {/* Paradas — só após existir a rota */}
          {rota.id && <Paradas rid={rota.id} paradas={detalhe?.clientes || []} />}
        </div>
      </div>
    </div>
  )
}

function Paradas({ rid, paradas: iniciais }: { rid: number; paradas: any[] }) {
  const qc = useQueryClient()
  const [lista, setLista] = useState<any[]>(iniciais)
  const [busca, setBusca] = useState('')
  useMemo(() => setLista(iniciais), [iniciais])

  const { data: achados } = useQuery<any>({
    queryKey: ['fv-busca-cli', busca],
    queryFn: async () => (await api.get('/forca-vendas/clientes/buscar', { params: { q: busca } })).data,
    enabled: busca.trim().length >= 2,
  })

  const salvar = useMutation({
    mutationFn: async () => (await api.put(`/forca-vendas/rotas/${rid}/clientes`, {
      clientes: lista.map(c => ({ codcli: c.codcli, cliente_nome: c.cliente_nome, municipio: c.municipio, latitude: c.latitude, longitude: c.longitude })),
    })).data,
    onSuccess: () => { toast.success('Paradas salvas'); qc.invalidateQueries({ queryKey: ['fv-rotas'] }); qc.invalidateQueries({ queryKey: ['fv-rota', rid] }) },
  })
  const otimizar = useMutation({
    mutationFn: async () => (await api.post(`/forca-vendas/rotas/${rid}/otimizar`)).data,
    onSuccess: (r) => { toast.success('Rota otimizada por proximidade'); setLista(r.clientes || []) },
    onError: (e: any) => toast.error(e?.response?.data?.detail?.msg || e?.response?.data?.msg || 'Poucos clientes com GPS pra otimizar'),
  })

  const add = (c: any) => {
    if (lista.some(x => x.codcli === c.codcli)) { toast('Cliente já está na rota'); return }
    setLista(l => [...l, { codcli: c.codcli, cliente_nome: c.cliente_nome, municipio: c.municipio, latitude: c.latitude ?? null, longitude: c.longitude ?? null }])
    if (!(c.latitude && c.longitude)) toast('Cliente sem GPS no app — não entra na otimização nem no mapa', { icon: '⚠️' })
    setBusca('')
  }
  const move = (i: number, dir: -1 | 1) => {
    const j = i + dir; if (j < 0 || j >= lista.length) return
    setLista(l => { const n = [...l];[n[i], n[j]] = [n[j], n[i]]; return n })
  }
  const rem = (i: number) => setLista(l => l.filter((_, idx) => idx !== i))

  return (
    <div className="border-t pt-4">
      <div className="flex items-center justify-between mb-2">
        <h4 className="font-semibold text-ink-700 text-sm flex items-center gap-1.5"><MapPin size={15} className="text-violet-600" /> Paradas ({lista.length})</h4>
        <div className="flex gap-2">
          <button onClick={() => otimizar.mutate()} disabled={otimizar.isPending}
            className="flex items-center gap-1 text-xs border border-ink-300 rounded-lg px-2.5 py-1.5 hover:bg-ink-50 disabled:opacity-40">
            <Wand2 size={13} /> Otimizar ordem
          </button>
          <button onClick={() => salvar.mutate()} disabled={salvar.isPending}
            className="flex items-center gap-1 text-xs bg-emerald-600 text-white rounded-lg px-2.5 py-1.5 hover:bg-emerald-700 disabled:opacity-40">
            {salvar.isPending ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Salvar paradas
          </button>
        </div>
      </div>

      {/* busca p/ adicionar */}
      <div className="relative mb-2">
        <Search size={14} className="absolute left-2.5 top-2.5 text-ink-400" />
        <input value={busca} onChange={e => setBusca(e.target.value)} placeholder="Buscar cliente por nome ou código…"
          onKeyDown={enterAbrePrimeiro(busca.trim().length >= 2 ? (achados?.rows || []) : [], add)}
          className="input pl-8 w-full" />
        {busca.trim().length >= 2 && (achados?.rows?.length ?? 0) > 0 && (
          <div className="absolute z-10 mt-1 w-full bg-white border border-ink-200 rounded-lg shadow-lg max-h-52 overflow-y-auto">
            {achados.rows.map((c: any) => (
              <button key={c.codcli} onClick={() => add(c)} className="w-full text-left px-3 py-1.5 text-sm hover:bg-violet-50 flex justify-between items-center gap-2">
                <span className="truncate"><b className="text-ink-700">{c.codcli}</b> · {c.cliente_nome}</span>
                <span className="flex items-center gap-1.5 shrink-0">
                  {(c.latitude && c.longitude)
                    ? <span className="text-emerald-500 text-[10px] font-semibold">●GPS</span>
                    : <span className="text-amber-500 text-[10px]">sem GPS</span>}
                  <span className="text-ink-400 text-xs">{c.municipio}{c.uf ? `/${c.uf}` : ''}</span>
                </span>
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="max-h-72 overflow-y-auto border border-ink-100 rounded-lg divide-y">
        {lista.map((c, i) => (
          <div key={c.codcli} className="flex items-center gap-2 px-2 py-1.5 text-sm hover:bg-ink-50">
            <span className="text-ink-300 w-6 text-right">{i + 1}</span>
            <span className="flex-1 truncate"><b className="text-ink-700">{c.codcli}</b> · {c.cliente_nome}
              <span className="text-ink-400 text-xs ml-1">{c.municipio}</span>
              {(c.latitude && c.longitude) ? <span className="text-emerald-500 text-[10px] ml-1">●GPS</span> : <span className="text-amber-500 text-[10px] ml-1">sem GPS</span>}
            </span>
            <button onClick={() => move(i, -1)} className="text-ink-300 hover:text-ink-600 p-0.5"><ArrowUp size={13} /></button>
            <button onClick={() => move(i, 1)} className="text-ink-300 hover:text-ink-600 p-0.5"><ArrowDown size={13} /></button>
            <button onClick={() => rem(i)} className="text-ink-300 hover:text-rose-600 p-0.5"><X size={14} /></button>
          </div>
        ))}
        {lista.length === 0 && <div className="text-center text-ink-400 text-sm py-6">Nenhuma parada. Busque clientes acima.</div>}
      </div>
    </div>
  )
}

// ============================================================================
// ABA 2 — MAPA PREVISTO × REALIZADO
// ============================================================================
function AbaMapa() {
  const [data, setData] = useState(hojeISO())
  const [userId, setUserId] = useState<number | null>(null)

  const { data: vends } = useQuery<any>({
    queryKey: ['fv-mapa-vends', data],
    queryFn: async () => (await api.get('/forca-vendas/mapa/vendedores', { params: { data } })).data,
  })
  const { data: mapa, isLoading } = useQuery<any>({
    queryKey: ['fv-mapa', userId, data],
    queryFn: async () => (await api.get('/forca-vendas/mapa', { params: { user_id: userId, data } })).data,
    enabled: !!userId,
  })

  const rows = vends?.rows || []
  // seleciona automaticamente quem mais rodou
  useMemo(() => { if (!userId && rows.length) setUserId(rows[0].user_id) }, [rows])

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 flex-wrap bg-white rounded-2xl border border-ink-200 p-3 shadow-sm">
        <Calendar size={16} className="text-ink-400" />
        <input type="date" value={data} max={hojeISO()} onChange={e => { setData(e.target.value); setUserId(null) }}
          className="text-sm border border-ink-300 rounded-lg px-3 py-1.5 bg-white" />
        <select value={userId ?? ''} onChange={e => setUserId(Number(e.target.value) || null)}
          className="text-sm border border-ink-300 rounded-lg px-3 py-1.5 bg-white min-w-[220px]">
          <option value="">Escolha o vendedor…</option>
          {rows.map((v: any) => <option key={v.user_id} value={v.user_id}>{v.vendedor} — {v.km} km</option>)}
        </select>
        {rows.length === 0 && <span className="text-xs text-ink-400">Ninguém com trajeto registrado neste dia.</span>}
      </div>

      {!userId ? (
        <div className="text-center py-16 text-ink-400"><Footprints size={40} className="mx-auto mb-2 opacity-40" /><p>Selecione um vendedor com trajeto no dia.</p></div>
      ) : isLoading || !mapa ? <CarregandoBox /> : <MapaConteudo m={mapa} />}
    </div>
  )
}

function MapaConteudo({ m }: { m: any }) {
  const traj: any[] = (m.trajeto || []).filter((p: any) => p.lat && p.lng)
  const checks: any[] = (m.checkins || []).filter((c: any) => c.tem_gps)
  const plan: any[] = (m.planejado || []).filter((p: any) => p.lat && p.lng)
  const visitados = (m.checkins || []).length
  const comPedido = (m.checkins || []).filter((c: any) => c.resultado === 'pedido').length
  const positiv = visitados ? Math.round(comPedido / visitados * 100) : 0
  const durs = (m.checkins || []).map((c: any) => c.duracao_seg).filter(Boolean)
  const tempoMedio = durs.length ? Math.round(durs.reduce((a: number, b: number) => a + b, 0) / durs.length) : 0

  const pts: [number, number][] = [
    ...traj.map((p: any) => [p.lat, p.lng] as [number, number]),
    ...checks.map((c: any) => [c.lat, c.lng] as [number, number]),
    ...plan.map((p: any) => [p.lat, p.lng] as [number, number]),
  ]
  const center: [number, number] = pts.length
    ? [pts.reduce((a, p) => a + p[0], 0) / pts.length, pts.reduce((a, p) => a + p[1], 0) / pts.length]
    : [-7.0, -39.0]

  const corCheck = (r: string | null) => (r === 'pedido' ? '#10b981' : r === 'sem_pedido' ? '#f59e0b' : '#94a3b8')

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Kpi icon={<Gauge size={16} />} cor="cyan" label="Km percorrido" valor={`${m.km_percorrido ?? 0} km`} />
        <Kpi icon={<Target size={16} />} cor="violet" label="Planejados" valor={String(plan.length || (m.planejado || []).length)} />
        <Kpi icon={<CheckCircle2 size={16} />} cor="blue" label="Visitados" valor={String(visitados)} sub={`${checks.length} com GPS`} />
        <Kpi icon={<TrendingUp size={16} />} cor="emerald" label="Positivação" valor={`${positiv}%`} sub={`${comPedido} c/ pedido`} />
        <Kpi icon={<Clock size={16} />} cor="amber" label="Tempo médio" valor={fmtDur(tempoMedio)} sub="por cliente" />
      </div>

      <div className="bg-white rounded-2xl border border-ink-200 shadow-sm overflow-hidden">
        <div className="px-4 py-2 border-b flex items-center gap-4 text-xs flex-wrap">
          <Legenda cor="#8b5cf6" tracejado texto="Rota planejada" />
          <Legenda cor="#3b82f6" texto="Trajeto percorrido (GPS)" />
          <Legenda cor="#10b981" ponto texto="Visitou e vendeu" />
          <Legenda cor="#f59e0b" ponto texto="Visitou, sem pedido" />
          <Legenda cor="#94a3b8" ponto texto="Parada planejada" />
        </div>
        <div style={{ height: 520 }}>
          <MapContainer center={center} zoom={11} style={{ height: '100%', width: '100%' }} scrollWheelZoom>
            <TileLayer attribution='&copy; OpenStreetMap' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            <FitBounds pts={pts} />

            {/* Planejado: linha tracejada + pontos cinza */}
            {plan.length > 1 && <Polyline positions={plan.map((p: any) => [p.lat, p.lng])} pathOptions={{ color: '#8b5cf6', weight: 2, dashArray: '6 8', opacity: 0.7 }} />}
            {plan.map((p: any, i: number) => (
              <CircleMarker key={`pl${i}`} center={[p.lat, p.lng]} radius={5} pathOptions={{ color: '#fff', weight: 1, fillColor: '#94a3b8', fillOpacity: 0.9 }}>
                <LTooltip>{p.ordem != null ? `${p.ordem + 1}. ` : ''}{p.cliente_nome}</LTooltip>
              </CircleMarker>
            ))}

            {/* Realizado: trajeto GPS */}
            {traj.length > 1 && <Polyline positions={traj.map((p: any) => [p.lat, p.lng])} pathOptions={{ color: '#3b82f6', weight: 3, opacity: 0.85 }} />}

            {/* Check-ins (visitas reais) */}
            {checks.map((c: any, i: number) => (
              <CircleMarker key={`ck${i}`} center={[c.lat, c.lng]} radius={8}
                pathOptions={{ color: '#fff', weight: 2, fillColor: corCheck(c.resultado), fillOpacity: 1 }}>
                <LTooltip>
                  <div className="text-xs">
                    <b>{c.cliente_nome}</b><br />
                    {hhmm(c.check_in_at)}{c.check_out_at ? `–${hhmm(c.check_out_at)}` : ''} · {fmtDur(c.duracao_seg)}<br />
                    {c.resultado === 'pedido' ? '✅ Fez pedido' : c.resultado === 'sem_pedido' ? '⚠️ Sem pedido' : 'Visita aberta'}
                    {c.fora_do_raio ? ' · fora do raio' : ''}
                  </div>
                </LTooltip>
              </CircleMarker>
            ))}
          </MapContainer>
        </div>
      </div>

      {/* Lista de visitas do dia */}
      <div className="bg-white rounded-2xl border border-ink-200 shadow-sm p-4">
        <h3 className="font-semibold text-ink-800 text-sm mb-2 flex items-center gap-1.5"><MapPin size={15} className="text-violet-600" /> Visitas do dia ({(m.checkins || []).length})</h3>
        {(m.checkins || []).length === 0 ? <Vazio txt="Nenhuma visita registrada" /> : (
          <table className="w-full text-sm">
            <thead><tr className="text-ink-500 text-xs border-b">
              <th className="text-left py-1.5">Cliente</th><th className="text-left">Entrada</th>
              <th className="text-left">Saída</th><th className="text-right">Tempo</th><th className="text-center">Resultado</th>
            </tr></thead>
            <tbody>
              {(m.checkins || []).map((c: any, i: number) => (
                <tr key={i} className="border-b last:border-0 hover:bg-ink-50">
                  <td className="py-1.5">{c.cliente_nome}{!c.tem_gps && <span className="text-amber-500 text-[10px] ml-1">sem GPS</span>}</td>
                  <td className="text-ink-500">{hhmm(c.check_in_at)}</td>
                  <td className="text-ink-500">{c.check_out_at ? hhmm(c.check_out_at) : <span className="text-ink-300">aberta</span>}</td>
                  <td className="text-right font-medium">{fmtDur(c.duracao_seg)}</td>
                  <td className="text-center">{c.resultado === 'pedido'
                    ? <span className="inline-flex items-center gap-0.5 text-emerald-600 text-xs font-semibold"><CheckCircle2 size={13} />Pedido</span>
                    : c.resultado === 'sem_pedido'
                      ? <span className="inline-flex items-center gap-0.5 text-amber-600 text-xs font-semibold"><XCircle size={13} />Sem pedido</span>
                      : <span className="text-ink-400 text-xs">—</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}

function FitBounds({ pts }: { pts: [number, number][] }) {
  const map = useMap()
  useMemo(() => {
    if (pts.length === 1) map.setView(pts[0], 14)
    else if (pts.length > 1) {
      const lats = pts.map(p => p[0]), lngs = pts.map(p => p[1])
      map.fitBounds([[Math.min(...lats), Math.min(...lngs)], [Math.max(...lats), Math.max(...lngs)]], { padding: [30, 30] })
    }
  }, [pts.length])
  return null
}

// ============================================================================
// ABA 3 — ADERÊNCIA
// ============================================================================
function AbaAderencia() {
  const now = new Date()
  const [ano, setAno] = useState(now.getFullYear())
  const [mes, setMes] = useState(now.getMonth() + 1)

  const { data, isLoading } = useQuery<any>({
    queryKey: ['fv-aderencia', ano, mes],
    queryFn: async () => (await api.get('/forca-vendas/aderencia', { params: { ano, mes } })).data,
  })

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 bg-white rounded-2xl border border-ink-200 p-3 shadow-sm">
        <Calendar size={16} className="text-ink-400" />
        <select value={mes} onChange={e => setMes(Number(e.target.value))} className="text-sm border border-ink-300 rounded-lg px-2 py-1.5 bg-white">
          {['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'].map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
        </select>
        <input type="number" value={ano} onChange={e => setAno(Number(e.target.value))} className="text-sm border border-ink-300 rounded-lg px-2 py-1.5 bg-white w-24" />
      </div>

      {isLoading || !data ? <CarregandoBox /> : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Kpi icon={<Target size={16} />} cor="violet" label="Aderência geral" valor={`${data.aderencia_geral_pct}%`} sub="visitados ÷ planejados" />
            <Kpi icon={<MapPin size={16} />} cor="blue" label="Planejados" valor={String(data.total_planejados)} />
            <Kpi icon={<CheckCircle2 size={16} />} cor="emerald" label="Visitados" valor={String(data.total_visitados)} />
            <Kpi icon={<Users size={16} />} cor="amber" label="Vendedores" valor={String((data.vendedores || []).length)} />
          </div>

          <div className="bg-white rounded-2xl border border-ink-200 shadow-sm p-4">
            <h3 className="font-semibold text-ink-800 text-sm mb-2">Aderência por rota</h3>
            <table className="w-full text-sm">
              <thead><tr className="text-ink-500 text-xs border-b">
                <th className="text-left py-1.5">Rota</th><th className="text-left">Vendedor</th><th className="text-left">Dia</th>
                <th className="text-right">Clientes</th><th className="text-right">Planej.</th><th className="text-right">Visit.</th><th className="text-left w-32">Aderência</th>
              </tr></thead>
              <tbody>
                {(data.rotas || []).map((r: any) => (
                  <tr key={r.rota_id} className="border-b last:border-0 hover:bg-ink-50">
                    <td className="py-1.5 font-medium"><span className="inline-block w-2.5 h-2.5 rounded-full mr-1.5 align-middle" style={{ background: r.cor }} />{r.nome}</td>
                    <td className="text-ink-600">{r.vendedor || '—'}</td>
                    <td className="text-ink-500">{r.dia_label}</td>
                    <td className="text-right">{r.qt_clientes}</td>
                    <td className="text-right text-ink-500">{r.planejados}</td>
                    <td className="text-right font-semibold">{r.visitados}</td>
                    <td><Barra v={r.aderencia_pct} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="bg-white rounded-2xl border border-ink-200 shadow-sm p-4">
            <h3 className="font-semibold text-ink-800 text-sm mb-2">Por vendedor — visitas em rota × fora de rota</h3>
            <table className="w-full text-sm">
              <thead><tr className="text-ink-500 text-xs border-b">
                <th className="text-left py-1.5">Vendedor</th><th className="text-right">Visitas</th><th className="text-right">Em rota</th>
                <th className="text-right">Fora de rota</th><th className="text-right">% fora</th><th className="text-right">Tempo médio</th>
              </tr></thead>
              <tbody>
                {(data.vendedores || []).map((v: any) => (
                  <tr key={v.user_id} className="border-b last:border-0 hover:bg-ink-50">
                    <td className="py-1.5 font-medium">{v.vendedor}</td>
                    <td className="text-right">{v.visitas}</td>
                    <td className="text-right text-emerald-600">{v.em_rota}</td>
                    <td className="text-right text-amber-600">{v.fora_de_rota}</td>
                    <td className="text-right">{v.fora_pct}%</td>
                    <td className="text-right">{fmtDur(v.tempo_medio_seg)}</td>
                  </tr>
                ))}
                {(data.vendedores || []).length === 0 && <tr><td colSpan={6}><Vazio txt="Sem visitas no mês" /></td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  )
}

// ============================================================================
// auxiliares
// ============================================================================
function Campo({ label, children }: any) {
  return <div><label className="text-xs text-ink-500 font-medium block mb-1">{label}</label>{children}</div>
}
function Kpi({ icon, cor, label, valor, sub }: any) {
  const cores: any = { violet: 'from-violet-500 to-violet-600', blue: 'from-blue-500 to-blue-600', emerald: 'from-emerald-500 to-emerald-600', amber: 'from-amber-500 to-amber-600', cyan: 'from-cyan-500 to-cyan-600' }
  return (
    <div className="bg-white rounded-2xl border border-ink-200 shadow-sm p-4">
      <div className="flex items-center gap-2">
        <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${cores[cor]} text-white grid place-items-center`}>{icon}</div>
        <span className="text-sm font-medium text-ink-600">{label}</span>
      </div>
      <div className="mt-2 text-2xl font-bold text-ink-800">{valor}</div>
      {sub && <div className="text-xs text-ink-500 mt-0.5">{sub}</div>}
    </div>
  )
}
function Barra({ v }: { v: number }) {
  const cor = v >= 80 ? '#10b981' : v >= 50 ? '#f59e0b' : '#ef4444'
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-2 rounded-full bg-ink-100 overflow-hidden min-w-[40px]">
        <div className="h-full rounded-full" style={{ width: `${Math.min(100, v)}%`, background: cor }} />
      </div>
      <span className="text-xs font-bold text-ink-700 w-10 text-right">{v}%</span>
    </div>
  )
}
function Legenda({ cor, texto, tracejado, ponto }: any) {
  return (
    <span className="flex items-center gap-1.5 text-ink-600">
      {ponto
        ? <span className="w-3 h-3 rounded-full border-2 border-white" style={{ background: cor, boxShadow: '0 0 0 1px #ddd' }} />
        : <span className="w-5 h-0" style={{ borderTop: `3px ${tracejado ? 'dashed' : 'solid'} ${cor}` }} />}
      {texto}
    </span>
  )
}
function Vazio({ txt = 'Sem dados' }: { txt?: string }) { return <div className="text-center text-ink-400 text-sm py-8">{txt}</div> }
function CarregandoBox() { return <div className="grid place-items-center py-20 text-ink-400"><Loader2 className="animate-spin" size={28} /></div> }
